import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { message } from "antd";
import { useTranslation } from "react-i18next";

import { resendCodeDoctor, verifyCodeDoctor, requestResetDoctor } from "../redux/thunks/doctorsThunks";
import { clearError } from "../redux/slices/doctorsSlice";
import { getDoctorRedirectTarget } from "../functions/getDoctorRedirectTarget";
import { CABINET_ROUTES } from "../../cabinetDashboard/routePaths";

export default function useDoctorVerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const email = location.state?.email || "";
  const userId = location.state?.userId || "";
  const tempToken = location.state?.tempToken || "";

  const is2FA = !!(tempToken || userId);

  const [code, setCode] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVerified, setIsVerified] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate("/ForgetPassword", { replace: true });
    }
  }, [email, navigate]);

  const maskedEmail = useMemo(() => {
    if (!email) return "";
    return email.replace(/(.{3})(.*)(@.*)/, "$1***$3");
  }, [email]);

  const codeFilled = useMemo(() => code.every((c) => String(c).length === 1), [code]);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const next = [...code];
    next[index] = value;
    setCode(next);

    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (idx) => (e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (e.key === "Enter") handleVerify();
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    if (/^[0-9]{6}$/.test(pasted)) {
      setCode(pasted.split(""));
      setTimeout(() => inputRefs.current[5]?.focus(), 0);
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (!email) {
      message.error(t("auth.emailMissing"));
      return;
    }

    setError("");
    const resetCode = code.join("").trim();

    if (resetCode.length !== 6) {
      setError(t("auth.enterCompleteCode") || "Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      if (location.state?.role === "doctor") {
        const payload = await dispatch(
          verifyCodeDoctor({ email: email.trim(), code: resetCode })
        ).unwrap();

        if (!payload?.accessToken) {
          throw new Error("Backend did not return accessToken");
        }

        setIsVerified(true);
        const redirectTarget = getDoctorRedirectTarget(location.state);
        if (redirectTarget.split(/[?#]/, 1)[0] === CABINET_ROUTES.DASHBOARD) {
          sessionStorage.setItem("dashboardWelcomeAnimation", "true");
        }
        navigate(redirectTarget, { replace: true });
        return;
      }

      setIsVerified(true);
      navigate("/SetNewPasswordDoctor", {
        state: { email: email.trim(), resetCode },
        replace: true,
      });
    } catch (err) {
      console.error("[VerifCodeDoctor] verify error", err);
      setError("Invalid or expired code");
      setCode(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      message.error(t("auth.emailMissing"));
      return;
    }

    setLoading(true);
    setError("");
    try {
      if (is2FA) {
        if (!userId) {
          message.error(t("auth.cannotResendMissingUser"));
          return;
        }
        await dispatch(resendCodeDoctor({ userId })).unwrap();
      } else {
        await dispatch(requestResetDoctor(email)).unwrap();
      }
      dispatch(clearError());

      setCode(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 0);

      message.success(t("auth.resendCodeSuccess") || "A new code has been sent to your email");
      return;
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.message || t("auth.resendCodeFailed") || "Failed to resend code";
      message.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    is2FA,
    code,
    errors: { general: error },
    isResending: loading,
    countdown: 0,
    inputRefs,
    setInputRef: (idx, el) => {
      inputRefs.current[idx] = el;
    },
    handleChange,
    handleKeyDown,
    handlePaste,
    handleVerify,
    handleResend,
    maskedEmail,
    codeFilled,
    isVerified,
  };
}
